import { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { uploadArtwork } from "@/components/signs/useSignConfig";
import { Field, Primary, inputCls } from "@/components/backoffice/kit";
import { FulfillmentSwitch } from "@/components/account/FulfillmentSwitch";

const MODES = [["per_piece", "Per piece"], ["per_sqft", "Per square foot"], ["flat", "Flat"]];

function PhotoPick({ photo, onPhoto, testid }) {
    const file = useRef(null);
    const [busy, setBusy] = useState(false);
    const up = async (f) => {
        if (!f) return;
        setBusy(true);
        try { const rec = await uploadArtwork(f); onPhoto(rec.url); } catch { toast.error("Upload failed. Try again."); } finally { setBusy(false); }
    };
    return (
        <div className="flex items-center gap-3">
            <div className="h-20 w-20 border border-[#1a1a1a] bg-white">{photo && <img src={photo} alt="" className="h-full w-full object-contain" />}</div>
            <button type="button" onClick={() => file.current?.click()} disabled={busy} data-testid={testid}
                className="inline-flex min-h-[44px] items-center gap-2 border border-[#1a1a1a] bg-white px-3 text-[15px] font-medium"><Upload size={16} /> {photo ? "Change photo" : "Upload photo"}</button>
            <input ref={file} type="file" accept="image/*" className="hidden" onChange={(e) => up(e.target.files?.[0])} />
        </div>
    );
}

/** 1.4 generic form — the six fields: photo, name, price, size/quantity, designer, note. `showFulfillment` adds the 13thav prints switch. */
export default function GenericProductForm({ onSaved, endpoint = "/store/catalog/custom", showFulfillment = true, rate = 0.2 }) {
    const [f, setF] = useState({ name: "", photo: "", price_mode: "per_piece", rows: [{ min_qty: "", unit_price: "" }], rate: "",
        sizes: "", qtys: "", designer: true, note: "", consign: true });
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const row = (n, k, v) => setF((s) => ({ ...s, rows: s.rows.map((r, i) => (i === n ? { ...r, [k]: v } : r)) }));
    const save = async () => {
        setErr(""); setBusy(true);
        try {
            const body = {
                name: f.name, photo: f.photo || null, price_mode: f.price_mode,
                price_table: f.price_mode === "per_piece" ? f.rows.filter((r) => r.unit_price !== "").map((r) => ({ min_qty: Number(r.min_qty || 1), unit_price: Number(r.unit_price) })) : null,
                rate: f.price_mode === "per_piece" ? null : Number(f.rate || 0),
                size_presets: f.sizes.split(",").map((s) => s.trim()).filter(Boolean),
                qty_presets: f.qtys.split(",").map((s) => Number(s.trim())).filter((n) => n > 0),
                designer_enabled: f.designer, note_example: f.note, fulfillment: f.consign ? "consignment" : "self",
            };
            const { data } = await api.post(endpoint, body);
            toast.success(`${f.name} added`);
            onSaved(data);
        } catch (e) { setErr(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
    };
    return (
        <div className="grid gap-4" data-testid="generic-form">
            <Field label="Photo"><PhotoPick photo={f.photo} onPhoto={(url) => setF({ ...f, photo: url })} testid="generic-photo" /></Field>
            <Field label="Name"><input className={inputCls} value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Restaurant Menus" data-testid="generic-name" /></Field>
            <Field label="Price">
                <div className="flex gap-2" data-testid="generic-price-modes">
                    {MODES.map(([k, l]) => (
                        <button key={k} type="button" onClick={() => setF({ ...f, price_mode: k })} data-testid={`generic-mode-${k}`}
                            className={`min-h-[44px] flex-1 border px-2 text-[14px] font-medium ${f.price_mode === k ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-[#1a1a1a] bg-white"}`}>{l}</button>
                    ))}
                </div>
                {f.price_mode === "per_piece" ? (
                    <div className="mt-2 grid gap-2" data-testid="generic-rows">
                        {f.rows.map((r, n) => (
                            <div key={n} className="grid grid-cols-2 gap-2">
                                <input type="number" inputMode="numeric" className={inputCls} placeholder="Quantity (25)" value={r.min_qty} onChange={(e) => row(n, "min_qty", e.target.value)} data-testid={`generic-qty-${n}`} />
                                <input type="number" inputMode="decimal" step="0.01" className={inputCls} placeholder="Each ($3.00)" value={r.unit_price} onChange={(e) => row(n, "unit_price", e.target.value)} data-testid={`generic-unit-${n}`} />
                            </div>
                        ))}
                        <button type="button" onClick={() => setF({ ...f, rows: [...f.rows, { min_qty: "", unit_price: "" }] })} data-testid="generic-add-row" className="min-h-[44px] justify-self-start underline">+ another quantity</button>
                    </div>
                ) : (
                    <input type="number" inputMode="decimal" step="0.01" className={`${inputCls} mt-2`} placeholder={f.price_mode === "per_sqft" ? "$ per square foot" : "$ flat price"} value={f.rate} onChange={(e) => setF({ ...f, rate: e.target.value })} data-testid="generic-rate" />
                )}
            </Field>
            <Field label="Sizes (optional)" hint="Comma-separated, W×H in inches — 8.5×11, 11×17"><input className={inputCls} value={f.sizes} onChange={(e) => setF({ ...f, sizes: e.target.value })} placeholder="8.5×11, 11×17" data-testid="generic-sizes" /></Field>
            <Field label="Quantity buttons (optional)" hint="Comma-separated — 25, 50, 100"><input className={inputCls} value={f.qtys} onChange={(e) => setF({ ...f, qtys: e.target.value })} placeholder="25, 50, 100" data-testid="generic-qtys" /></Field>
            <Field label="Designer">
                <div className="flex gap-2">
                    {[[true, "On"], [false, "Off — upload only"]].map(([v, l]) => (
                        <button key={l} type="button" onClick={() => setF({ ...f, designer: v })} data-testid={`generic-designer-${v ? "on" : "off"}`}
                            className={`min-h-[44px] flex-1 border px-2 text-[14px] font-medium ${f.designer === v ? "border-[#1a1a1a] bg-[#1a1a1a] text-white" : "border-[#1a1a1a] bg-white"}`}>{l}</button>
                    ))}
                </div>
            </Field>
            <Field label="Note example" hint="Shown inside the customer's note box"><input className={inputCls} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Example: laminated, 2-sided, folded in half" data-testid="generic-note" /></Field>
            {showFulfillment && (
                <Field label="13thav prints this">
                    <FulfillmentSwitch on={f.consign} onChange={(v) => setF({ ...f, consign: v })} testid="generic-fulfillment" />
                </Field>
            )}
            {err && <p className="text-[15px] text-[#b3261e]" data-testid="generic-error">{err}</p>}
            <Primary onClick={save} disabled={busy} testid="generic-save" full>Save</Primary>
        </div>
    );
}
